<script setup lang="ts">
import { ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { inventoryApi, type InventoryCategory } from '@/api/inventory';
import { apiErrorText } from '@/utils/api-errors';

const emit = defineEmits<{ close: [] }>();

const queryClient = useQueryClient();
const newName = ref('');
const editing = ref<{ id: number; name: string } | null>(null);
const error = ref('');
const showArchived = ref(false);

const { data: categories } = useQuery({
  queryKey: ['inventoryCategories', 'active'],
  queryFn: () => inventoryApi.categories(),
  initialData: [],
});

const { data: archivedCategories } = useQuery({
  queryKey: ['inventoryCategories', 'discarded'],
  queryFn: () => inventoryApi.categories('discarded'),
  initialData: [],
});

function refresh() {
  error.value = '';
  queryClient.invalidateQueries({ queryKey: ['inventoryCategories'] });
  queryClient.invalidateQueries({ queryKey: ['inventoryItems'] });
}

function fail(failure: unknown) {
  error.value = apiErrorText(failure, 'No se pudo guardar la categoría.');
}

const save = useMutation({
  mutationFn: (category: { id?: number; name: string }) => inventoryApi.saveCategory(category),
  onSuccess: () => {
    newName.value = '';
    editing.value = null;
    refresh();
  },
  onError: fail,
});

// Updating an archived category restores it.
const restore = useMutation({
  mutationFn: (category: InventoryCategory) => inventoryApi.saveCategory({ id: category.id, name: category.name }),
  onSuccess: refresh,
  onError: fail,
});

// Caregivers see in the app the stock of visible categories.
const toggleVisible = useMutation({
  mutationFn: (category: InventoryCategory) => inventoryApi.saveCategory({
    id: category.id, name: category.name, visibleToCaregivers: !category.visibleToCaregivers,
  }),
  onSuccess: refresh,
  onError: fail,
});

const archive = useMutation({
  mutationFn: (category: InventoryCategory) => inventoryApi.archiveCategory(category.id),
  onSuccess: refresh,
  onError: fail,
});
</script>
<template>
  <v-card title="Categorías">
    <v-card-text class="pt-4">
      <div class="text-body-2 text-medium-emphasis">
        <v-icon
          icon="mdi-cellphone"
          size="small"
        />
        Los profesionales ven en la app el stock de las categorías visibles.
      </div>
      <v-list density="compact">
        <v-list-item
          v-for="category in categories"
          :key="category.id"
        >
          <v-text-field
            v-if="editing?.id === category.id"
            v-model="editing.name"
            density="compact"
            variant="outlined"
            hide-details
            autofocus
            @keyup.enter="save.mutate(editing)"
          />
          <span v-else>{{ category.name }}</span>
          <template #append>
            <v-btn
              :icon="category.visibleToCaregivers ? 'mdi-cellphone' : 'mdi-cellphone-off'"
              :color="category.visibleToCaregivers ? 'primary' : undefined"
              variant="text"
              size="small"
              :aria-label="category.visibleToCaregivers ? 'Visible en la app: ocultar' : 'Oculta en la app: mostrar'"
              :title="category.visibleToCaregivers ? 'Visible en la app' : 'Oculta en la app'"
              @click="toggleVisible.mutate(category)"
            />
            <v-btn
              v-if="editing?.id === category.id"
              icon="mdi-check"
              variant="text"
              size="small"
              aria-label="Guardar"
              @click="save.mutate(editing)"
            />
            <v-btn
              v-else
              icon="mdi-pencil"
              variant="text"
              size="small"
              aria-label="Renombrar"
              @click="editing = { id: category.id, name: category.name }"
            />
            <v-btn
              icon="mdi-archive"
              variant="text"
              size="small"
              color="warning"
              aria-label="Archivar"
              @click="archive.mutate(category)"
            />
          </template>
        </v-list-item>
      </v-list>
      <template v-if="archivedCategories.length">
        <v-btn
          variant="text"
          size="small"
          :prepend-icon="showArchived ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          @click="showArchived = !showArchived"
        >
          Archivadas ({{ archivedCategories.length }})
        </v-btn>
        <v-list
          v-if="showArchived"
          density="compact"
        >
          <v-list-item
            v-for="category in archivedCategories"
            :key="category.id"
            :title="category.name"
            class="text-medium-emphasis"
          >
            <template #append>
              <v-btn
                size="small"
                variant="text"
                color="primary"
                prepend-icon="mdi-archive-arrow-up"
                :loading="restore.isPending.value && restore.variables.value?.id === category.id"
                @click="restore.mutate(category)"
              >
                Restaurar
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </template>
      <div class="mt-4 d-flex align-center ga-2">
        <v-text-field
          v-model="newName"
          label="Nueva categoría"
          density="compact"
          variant="outlined"
          hide-details
          @keyup.enter="newName.trim() && save.mutate({ name: newName })"
        />
        <v-btn
          color="primary"
          :disabled="!newName.trim()"
          @click="save.mutate({ name: newName })"
        >
          Agregar
        </v-btn>
      </div>
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-3"
        :text="error"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn @click="emit('close')">
        Cerrar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
